import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getBackfillPlan, runBackfill, getBackfillStatus } from "@/lib/adminApi";
import { apiErrorText, formatUsd, backfillCap, backfillButtonState, backfillProgressText } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

const POLL_MS = 3000;

export default function Backfill() {
  useNoindex();
  const [plan, setPlan] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wasRunning = useRef(false);

  const loadPlan = useCallback(async () => {
    try { setPlan(await getBackfillPlan()); setError(""); } catch (e) { setError(apiErrorText(e, "Plan yüklenemedi")); }
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await getBackfillStatus();
        if (!alive) return;
        setStatus(s);
        if (wasRunning.current && s.state !== "running") loadPlan();
        wasRunning.current = s.state === "running";
      } catch { /* keep last known status */ }
    };
    loadPlan();
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [loadPlan]);

  const start = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      await runBackfill(backfillCap(plan.estimated_usd));
      setStatus(await getBackfillStatus());
      wasRunning.current = true;
      toast.success("Çalışma başladı");
    } catch (e) {
      toast.error(apiErrorText(e, "Çalışma başlatılamadı"));
    } finally {
      setBusy(false);
    }
  };

  const btn = backfillButtonState(plan, status, busy);
  const amount = plan ? formatUsd(plan.estimated_usd) : "";

  return (
    <main className="mx-auto max-w-5xl px-4 py-6" data-testid="backfill-page">
      <h1 className="mb-1 text-xl font-semibold">Eksik ölçümler</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Rapor linklerindeki eksik ölçümleri toplu tamamlar. Bu işlem gerçek para harcar.
      </p>
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {!plan && !error && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {plan && (
        <>
          <div className="mb-4 rounded-md border p-4" data-testid="backfill-summary">
            <div className="text-sm">
              <strong>{plan.links_to_complete}</strong> / {plan.links_total} link tamamlanacak.
              Tahmini maliyet: <strong>{amount}</strong>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">Google Places detay ücreti ayrıca faturalanır.</div>
            <Button className="mt-3" disabled={btn.disabled} onClick={() => setConfirmOpen(true)} data-testid="backfill-start">
              {btn.label}
            </Button>
          </div>

          {status && status.state !== "idle" && (
            <div className="mb-4 rounded-md border p-4 text-sm" data-testid="backfill-status">
              <div className={status.state === "error" ? "text-red-600" : ""}>{backfillProgressText(status)}</div>
              {status.errors && status.errors.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-xs text-red-600" data-testid="backfill-errors">
                  {status.errors.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              )}
            </div>
          )}

          {plan.items.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>İşletme</TableHead>
                  <TableHead>Eksik</TableHead>
                  <TableHead className="text-right">Tahmin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.items.map((it) => (
                  <TableRow key={it.token}>
                    <TableCell>{it.name}</TableCell>
                    <TableCell className="text-xs">{it.missing.join(", ") || "-"}</TableCell>
                    <TableCell className="text-right">{formatUsd(it.est_usd)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerçek para harcanacak</AlertDialogTitle>
            <AlertDialogDescription>
              {plan ? `${plan.links_to_complete} link için tahmini ${amount} harcanacak (DataForSEO). ` : ""}
              Bu gerçek paradır; Google Places detay ücreti ayrıca faturalanır. Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={start} data-testid="backfill-confirm">Evet, {amount} harca ve başlat</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
